// Desktop Organization Side Panel - matching admin-web EditOrganization pattern
import React, { useState, useCallback } from 'react';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelTabs } from '@/components/common/admin/desktop/panel/DesktopPanelTabs';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { IOrganization } from '@/types/admin/organizations';
import { useToast } from '@/contexts/admin/ToastContext';
import { 
  usePutOrganizationMutation, 
  useDeleteOrganizationMutation,
  useLazyGetOrganizationPropertiesQuery,
  useLazyGetOrganizationAdminsQuery,
} from '@/store/api/admin/organizationsApi';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { OrganizationInfoTab } from './OrganizationInfoTab';
import { OrganizationUsersTab } from './OrganizationUsersTab';
import { OrganizationPropertiesTab } from './OrganizationPropertiesTab';
import { ORGANIZATION_TABS } from '@/constants/admin';

interface DesktopOrganizationSidePanelProps {
  visible: boolean;
  onClose: () => void;
  organization: IOrganization;
  onSuccess?: () => void;
}

export const DesktopOrganizationSidePanel: React.FC<DesktopOrganizationSidePanelProps> = ({
  visible,
  onClose,
  organization,
  onSuccess,
}) => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Mutations
  const [updateOrganization, { isLoading: isUpdating }] = usePutOrganizationMutation();
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();

  // Queries
  const [getProperties, { data: propertiesData, isLoading: isLoadingProperties }] = 
    useLazyGetOrganizationPropertiesQuery();
  const [getAdmins, { data: adminsData, isLoading: isLoadingAdmins }] = 
    useLazyGetOrganizationAdminsQuery();

  // Load data when tab changes
  const handleTabChange = useCallback((index: number) => {
    setActiveTab(index);
    if (index === 1) {
      getProperties(organization.id);
    } else if (index === 2) {
      getAdmins(organization.id);
    }
  }, [organization.id, getProperties, getAdmins]);

  // Handle organization update
  const handleUpdate = useCallback(async (updatedOrg: IOrganization) => {
    try {
      await updateOrganization(updatedOrg).unwrap();
      showSuccess(`Organization "${updatedOrg.name}" updated successfully`);
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to update organization';
      showError(errorMessage);
    }
  }, [updateOrganization, showSuccess, showError, onSuccess]);

  // Handle organization delete
  const handleDelete = useCallback(async () => {
    try {
      await deleteOrganization(organization.id).unwrap();
      showSuccess(`Organization "${organization.name}" deleted successfully`);
      setShowDeleteDialog(false);
      onClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete organization';
      showError(errorMessage);
    }
  }, [deleteOrganization, organization, showSuccess, showError, onClose, onSuccess]);

  return (
    <>
      <DesktopSidePanel
        visible={visible}
        onClose={onClose}
        width={520}
        closeOnOverlayPress={!showDeleteDialog}
      >
        <DesktopPanelHeader
          title={organization.name}
          onClose={onClose}
        />

        <DesktopPanelTabs
          tabs={ORGANIZATION_TABS}
          activeIndex={activeTab}
          onChange={handleTabChange}
        />

        <DesktopPanelBody>
          {activeTab === 0 && (
            <OrganizationInfoTab
              organization={organization}
              onUpdate={handleUpdate}
              onDelete={() => setShowDeleteDialog(true)}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          )}

          {activeTab === 1 && (
            <OrganizationPropertiesTab
              organizationId={organization.id}
              properties={propertiesData?.response || []}
              isLoading={isLoadingProperties}
            />
          )}

          {activeTab === 2 && (
            <OrganizationUsersTab
              organizationId={organization.id}
              admins={adminsData?.response || []}
              isLoading={isLoadingAdmins}
            />
          )}
        </DesktopPanelBody>
      </DesktopSidePanel>

      {/* Delete Confirmation Modal */}
      <WebConfirmModal
        visible={showDeleteDialog}
        title="Delete Organization"
        message={`Are you sure you want to delete "${organization.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

